import {
  forwardRef,
  useImperativeHandle,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { Box, VStack, Text } from "@chakra-ui/react";
import AudioPlayer from "react-h5-audio-player";
import AudioItem from "./AudioItem";
import { MediaFile } from "../types/audioTypes";
import { useGlobalAudioStore } from "../store/useGlobalAudioStore";

interface AudioPlayerComponentProps {
  audioFiles: MediaFile[];
  onAudioStop?: () => void;
}

export interface AudioPlayerComponentRef {
  stopAllAudio: () => void;
}

const AudioPlayerComponent = forwardRef<
  AudioPlayerComponentRef,
  AudioPlayerComponentProps
>(({ audioFiles, onAudioStop }, ref) => {
  const [currentPlayingIndex, setCurrentPlayingIndex] = useState<number | null>(
    null
  );
  const [hasLoadedAll, setHasLoadedAll] = useState(false);
  const audioRefs = useRef<(AudioPlayer | null)[]>([]);
  const setCurrentPlayingAudio = useGlobalAudioStore(
    (state) => state.setCurrentPlayingAudio
  );

  const stopAllAudio = useCallback(() => {
    audioRefs.current.forEach((playerRef) => {
      if (playerRef?.audio?.current) {
        playerRef.audio.current.pause();
        playerRef.audio.current.currentTime = 0;
      }
    });
    setCurrentPlayingIndex(null);
    onAudioStop?.();
  }, [onAudioStop]);

  const stopAllAudioRef = useRef({ stopAllAudio });

  useEffect(() => {
    stopAllAudioRef.current = { stopAllAudio };
  }, [stopAllAudio]);

  useEffect(() => {
    return () => {
      stopAllAudio();
      setCurrentPlayingAudio(null);
    };
  }, [setCurrentPlayingAudio, stopAllAudio]);

  const handlePlaybackStart = (index: number) => {
    audioRefs.current.forEach((playerRef, i) => {
      if (i !== index && playerRef?.audio?.current) {
        playerRef.audio.current.pause();
      }
    });

    setCurrentPlayingIndex(index);
    setCurrentPlayingAudio(stopAllAudioRef.current);

    if (!hasLoadedAll) {
      audioRefs.current.forEach((playerRef, i) => {
        if (i === index) return;
        if (playerRef?.audio?.current) {
          playerRef.audio.current.load();
        }
      });
      setHasLoadedAll(true);
    }
  };

  const handlePlaybackEnd = (index: number) => {
    const nextIndex = index + 1;

    if (nextIndex < audioFiles.length) {
      setCurrentPlayingIndex(nextIndex);
      const nextPlayer = audioRefs.current[nextIndex];
      if (nextPlayer?.audio?.current) {
        nextPlayer.audio.current.play();
      }
    } else {
      setCurrentPlayingIndex(null);
    }
  };

  useImperativeHandle(ref, () => ({
    stopAllAudio,
  }));

  return (
    <Box
      width="100%"
      height="100%"
      padding="20px"
      backgroundColor="white"
      overflowY="auto"
    >
      <Text
        fontSize="xl"
        fontWeight="bold"
        marginBottom="20px"
        color="#333"
        borderBottom="2px solid #0096e0"
        paddingBottom="10px"
      >
        Customer Voice Notes
      </Text>

      <VStack spacing={3} align="stretch" width="100%">
        {audioFiles.map((audioFile, index) => (
          <AudioItem
            key={audioFile._id}
            ref={(el) => (audioRefs.current[index] = el)}
            index={index}
            audioUrl={audioFile.path}
            isActive={currentPlayingIndex === index}
            onPlaybackEnd={handlePlaybackEnd}
            onPlaybackStart={handlePlaybackStart}
          />
        ))}
      </VStack>
    </Box>
  );
});

AudioPlayerComponent.displayName = "AudioPlayerComponent";

export default AudioPlayerComponent;
